import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { type ApiError, api, type CompleteOnboardingResult, type GetOnboardingResult } from '@whinerp/client';
import { Button, ErrorState, Icon, SerifMoment, Skeleton, cx } from '@whinerp/ui';
import type { MessageKey } from '@whinerp/contracts/i18n';
import { useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-03 Setup wizard.
 *
 * Five questions, one per screen, large option cards, progress dots. Never a
 * five-field form: this is the highest-leverage screen in the product and a
 * form makes it feel like paperwork on the first day.
 *
 * The result screen reports what was applied, what was skipped because the plan
 * excludes it, and what is waiting on a module this deployment has not built.
 * Three outcomes, never two - claiming to have installed ten automations when
 * there is no automation engine would be a lie told on the first screen.
 */

function WizardScreen(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});

  const onboarding = useQuery<GetOnboardingResult, ApiError>({
    queryKey: ['onboarding'],
    queryFn: () => api.getOnboarding(),
  });

  const complete = useMutation<CompleteOnboardingResult, ApiError>({
    mutationFn: () =>
      api.completeOnboarding({
        body: {
          industry: answers['industry']?.[0] ?? 'other',
          teamSize: answers['teamSize']?.[0] ?? 'small',
          sells: answers['sells']?.[0] ?? 'services',
          cycle: answers['cycle']?.[0] ?? 'weeks',
          channels: answers['channels'] ?? [],
        },
      }),
  });

  if (onboarding.isPending) return <WizardLoading />;
  if (onboarding.isError) {
    return (
      <WizardFrame>
        <ErrorState
          title={t('state.error')}
          body={onboarding.error.message}
          onRetry={() => void onboarding.refetch()}
          retryLabel={t('action.retry')}
        />
      </WizardFrame>
    );
  }

  if (complete.data) return <WizardResult report={complete.data} onDone={() => navigate('/')} />;

  const questions = onboarding.data.questions;
  const question = questions[index];
  if (!question) return <WizardLoading />;

  const chosen = answers[question.key] ?? [];
  const canContinue = question.multiple || chosen.length > 0;

  const choose = (value: string): void => {
    setAnswers((current) => {
      const existing = current[question.key] ?? [];
      if (question.multiple) {
        return {
          ...current,
          [question.key]: existing.includes(value)
            ? existing.filter((v) => v !== value)
            : [...existing, value],
        };
      }
      return { ...current, [question.key]: [value] };
    });
    // A single-choice answer advances on its own: asking somebody to pick and
    // then press Next is one interaction too many, five times over.
    if (!question.multiple) {
      window.setTimeout(() => setIndex((i) => Math.min(i + 1, questions.length - 1)), 120);
    }
  };

  const isLast = index === questions.length - 1;

  return (
    <WizardFrame>
      <div data-component="WizardProgress" className="flex items-center gap-2">
        {questions.map((q, i) => (
          <span
            key={q.key}
            aria-hidden
            className={cx(
              'h-1 flex-1 rounded-chip transition-colors duration-state ease-out',
              i <= index ? 'bg-bronze' : 'bg-sunken',
            )}
          />
        ))}
      </div>
      <p className="text-micro uppercase tracking-label text-ink-label">
        {t('onboarding.progress', {
          values: { current: String(index + 1), total: String(questions.length) },
        })}
      </p>

      {/* Sans, deliberately. Every one of these is a question put to a
          person - a sentence - and the serif sets figures, titles and names.
          A display face asking "What does your business do?" is the moment it
          stops being a display face. */}
      <h2 className="text-h3 font-regular text-ink">{t(question.titleKey as MessageKey)}</h2>

      <ul data-component="WizardOptions" className="flex flex-col gap-2">
        {question.options.map((option) => {
          const selected = chosen.includes(option.value);
          return (
            <li key={option.value}>
              <button
                type="button"
                aria-pressed={selected}
                onClick={() => choose(option.value)}
                className={cx(
                  'flex w-full items-start gap-3 rounded-card border p-4 text-start',
                  'transition-colors duration-state ease-out',
                  selected
                    ? 'border-bronze bg-bronze-tint'
                    : 'border-control bg-surface hover:bg-sunken',
                )}
              >
                <span className={cx('mt-px shrink-0', selected ? 'text-bronze-text' : 'text-ink-400')}>
                  <Icon name={option.icon} decorative />
                </span>
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="text-lead font-medium text-ink">{option.label}</span>
                  <span className="text-dense text-ink-meta">{option.hint}</span>
                </span>
                {selected ? (
                  <span className="ms-auto shrink-0 text-bronze-text">
                    <Icon name="Check" size="inline" decorative />
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center gap-3">
        {index > 0 ? (
          <Button intent="ghost" icon="ArrowLeft" onClick={() => setIndex((i) => i - 1)}>
            {t('action.back')}
          </Button>
        ) : null}
        {isLast ? (
          <Button
            intent="primary"
            busy={complete.isPending}
            disabled={!canContinue}
            onClick={() => complete.mutate()}
          >
            {t('action.done')}
          </Button>
        ) : (
          <Button
            intent="primary"
            disabled={!canContinue}
            onClick={() => setIndex((i) => Math.min(i + 1, questions.length - 1))}
          >
            {t('action.next')}
          </Button>
        )}
        <button
          type="button"
          onClick={() => navigate('/')}
          className="ms-auto text-dense text-ink-meta underline decoration-rule underline-offset-4"
        >
          {t('action.skip')}
        </button>
      </div>

      {complete.isError ? (
        <ErrorState
          title={t('onboarding.failed')}
          body={complete.error.message}
          onRetry={() => complete.mutate()}
          retryLabel={t('action.retry')}
        />
      ) : null}
    </WizardFrame>
  );
}

/**
 * What the pack actually did.
 *
 * Grouped by outcome so the three are visibly different things rather than one
 * list with three colours in it.
 */
function WizardResult({
  report,
  onDone,
}: {
  report: CompleteOnboardingResult;
  onDone: () => void;
}): JSX.Element {
  const t = useT();
  const groups = [
    { status: 'applied' as const, label: 'Set up', icon: 'Check', tone: 'text-success' },
    { status: 'skipped' as const, label: 'Not on your plan', icon: 'Lock', tone: 'text-ink-meta' },
    { status: 'waiting' as const, label: 'Waiting on a module', icon: 'Clock', tone: 'text-warning' },
  ];

  return (
    <WizardFrame>
      <SerifMoment>{report.packLabel}</SerifMoment>
      {groups.map((group) => {
        const items = report.items.filter((item) => item.outcome.status === group.status);
        if (items.length === 0) return null;
        return (
          <section key={group.status} className="flex flex-col gap-2">
            <h2 className="text-micro uppercase tracking-label text-ink-label">{group.label}</h2>
            <ul className="flex flex-col gap-1">
              {items.map((item, i) => (
                <li key={`${item.label}-${String(i)}`} className="flex items-start gap-2 text-body">
                  <span className={cx('mt-px shrink-0', group.tone)}>
                    <Icon name={group.icon} size="inline" decorative />
                  </span>
                  <span className="text-ink">{item.label}</span>
                  <span className="text-ink-meta">
                    {item.outcome.status === 'applied' ? item.outcome.detail : item.outcome.reason}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      <Button intent="primary" onClick={onDone}>
        {t('action.continue')}
      </Button>
    </WizardFrame>
  );
}

function WizardFrame({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <main className="flex min-h-screen justify-center bg-canvas p-5">
      <div
        data-component="SetupWizard"
        className="flex w-full max-w-reading flex-col gap-5 self-center rounded-card bg-surface p-6"
      >
        {children}
      </div>
    </main>
  );
}

function WizardLoading(): JSX.Element {
  const t = useT();
  return (
    <main className="flex min-h-screen justify-center bg-canvas p-5" role="status" aria-busy aria-live="polite">
      <span className="sr-only">{t('state.loading')}</span>
      <div className="flex w-full max-w-reading flex-col gap-5 self-center rounded-card bg-surface p-6">
        <Skeleton className="h-1 w-full" rounded="chip" />
        <Skeleton className="h-line-h2 w-3/4" />
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-touch w-full" rounded="card" />
        ))}
      </div>
    </main>
  );
}

export const screen = defineScreen({
  id: 'SC-03',
  route: '/onboarding',
  surface: 'page',
  // Full screen, one question per view, no navigation rail (04_Screens).
  chrome: 'bare',
  title: 'Setup wizard',
  features: ['F-149'],
  entitlement: 'always-on',
  // Reached straight after signup, and again from Setup whenever somebody
  // wants to re-run it: F-149 requires it to be re-runnable.
  reachedBy: [
    { kind: 'screen', from: 'SC-01', how: 'straight after the workspace is created' },
    { kind: 'setup', section: 'Guided setup' },
  ],
  components: [],
  elements: ['SetupWizard', 'WizardProgress', 'WizardOptions'],
  states: {
    empty: { notApplicable: 'The questions are fixed; there is never a wizard with nothing in it.' },
    loading: WizardLoading,
    error: {
      notApplicable:
        'A failed step renders inline and keeps every answer already given, as F-149 requires. Replacing the page would lose them.',
    },
  },
  component: WizardScreen,
});

export default screen;
